import React from 'react';
import { statoAgente, motivoAgente, fmtEta } from './campagneAgenteData';

/* La barra del lavoro in sottofondo (il motore che indaga le righe accese).

   L'anello nella riga in alto (BulkProgress) dice a che punto e'; QUESTA barra dice
   perche' si e' fermato. `fermo_per_blocco` (Google chiede il captcha) e `fermo_per_errori`
   (errori ripetuti) sono gli unici due modi in cui il lavoro si ferma da solo, e
   vanno letti in chiaro sulla pagina: finiti in un log, l'utente vedrebbe solo un lavoro
   che "non fa niente". Restano scritti anche a lavoro finito, finche' non se ne avvia un
   altro: sono il motivo per cui quel lavoro e' durato meno del previsto. */

const fmt = (n) => (n ?? 0).toLocaleString('it-IT');

/* Quando si e' fermato. Senza la data la barra "Ultimo lavoro fermato" resta in memoria
   finche' il backend non riparte: fra una settimana si legge come se fosse appena
   successo. `fine` il backend ce l'ha gia' (stato_bulk), bastava stamparlo. */
const fmtData = (at) => {
  if (!at) return '';
  const d = new Date(at);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('it-IT', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

/* L'agente Campagne acceso: niente percentuale (non finisce mai), ma cosa sta facendo, quante
   righe accese restano e il motivo in chiaro quando aspetta un captcha o e' fermo per errori.
   Lo stato e' quello di /campagne/agente, lo stesso della spia nello sportellino. */
function BarraAgente({ agente }) {
  const st = statoAgente(agente);
  if (st.chiave === 'spento') return null;
  const motivo = motivoAgente(agente);
  const cls = st.chiave === 'errori' ? 'camp-box-err' : st.chiave === 'standby' ? 'camp-box-warn' : 'camp-box-info';
  return (
    <div className="camp-job">
      <div className="camp-job-riga">
        <span className="camp-job-tit">
          Agente Da qualificare · <b>{st.etichetta}</b>
          {' · '}<b>{fmt(agente.restano)}</b> righe accese da fare
          {agente.restano ? <> · {fmtEta(agente.eta_minuti)}</> : null}
          {agente.lavorate_24h ? <> · <b>{fmt(agente.lavorate_24h)}</b> lavorate e <b>{fmt(agente.ricostruiti_24h)}</b> numeri ricostruiti nelle ultime 24 ore</> : null}
        </span>
      </div>
      {st.chiave === 'attivo' && agente.corrente && (
        <div className="camp-nota">Sto guardando: <b>{agente.corrente}</b></div>
      )}
      {motivo && <div className={`camp-box ${cls}`}>{motivo}</div>}
    </div>
  );
}

export default function CampagnaJob({ job, onFerma, fermando, agente }) {
  const j = job || {};
  // `annullato` = fermato a mano. E' un'interruzione come le altre due, e va detta: il
  // lavoro non e' finito, si e' fermato dove l'hanno fermato.
  const fermo = j.fermo_per_blocco || j.fermo_per_errori || j.annullato;
  const barraAgente = <BarraAgente agente={agente} />;
  if (!j.attivo && !fermo) return barraAgente;
  // con l'agente acceso il vecchio "ultimo lavoro fermato" non serve piu': l'agente riprende
  // da solo le righe che quel lavoro aveva lasciato
  if (!j.attivo && agente?.attivo) return barraAgente;
  const perc = j.totale ? Math.min(100, Math.round(((j.fatti || 0) / j.totale) * 100)) : 0;
  const quando = j.attivo ? '' : fmtData(j.fine);

  return (
    <>
    <div className="camp-job">
      <div className="camp-job-riga">
        <span className="camp-job-tit">
          {j.attivo
            ? <>Indagine in corso · <b>{fmt(j.fatti)}</b> di {fmt(j.totale)}</>
            : <>Ultimo lavoro fermato · <b>{fmt(j.fatti)}</b> righe fatte di {fmt(j.totale)}</>}
          {j.risolti ? <> · <b>{fmt(j.risolti)}</b> numeri ricostruiti</> : null}
          {quando ? <span className="camp-nota-inline">{quando}</span> : null}
        </span>
        <div className="camp-spazio" />
        {j.attivo && <span className="camp-job-perc">{perc}%</span>}
        {j.attivo && (
          <button type="button" className="btn btn-secondary camp-btn-mini"
            disabled={fermando || !!j.annullato} onClick={onFerma}
            title="La riga in corso viene finita, poi il lavoro si ferma">
            {j.annullato ? 'Mi sto fermando…' : fermando ? 'Fermo…' : 'Ferma il lavoro'}
          </button>
        )}
      </div>

      {j.attivo && (
        <div className="camp-job-pista">
          <div className="camp-job-arco" style={{ width: `${perc}%` }} />
        </div>
      )}

      {j.attivo && j.corrente && (
        <div className="camp-nota">Sto guardando: <b>{j.corrente}</b></div>
      )}

      {j.standby && (
        <div className="camp-box camp-box-warn">
          Google chiede il captcha: il lavoro è in attesa e riproverà da solo fra un quarto
          d’ora. Non è stato perso niente.
        </div>
      )}
      {j.fermo_per_blocco && (
        <div className="camp-box camp-box-err">
          Lavoro fermato: Google continua a chiedere il captcha.
          {j.ultimo_errore ? <> {j.ultimo_errore}</> : null} Conviene riprovare più tardi.
        </div>
      )}
      {/* Fermato a mano: non e' un guasto, ma nemmeno una fine. Il lavoro riparte da
          «Avvia» e riprende dalle righe accese che sono rimaste da indagare. */}
      {j.annullato && !j.attivo && !j.fermo_per_blocco && !j.fermo_per_errori && (
        <div className="camp-box camp-box-warn">
          Lavoro fermato da te. Le righe non lavorate sono rimaste come stavano: premendo
          «Avvia» il lavoro riprende da lì.
        </div>
      )}
      {j.fermo_per_errori && (
        <div className="camp-box camp-box-err">
          Lavoro fermato: errori ripetuti nella lavorazione.
          {j.ultimo_errore ? <> {j.ultimo_errore}</> : null} Le righe non lavorate sono
          rimaste come stavano.
        </div>
      )}
    </div>
    {barraAgente}
    </>
  );
}
